import React from "react";
import { connect } from "react-redux";
import { Button, Form, Col, Card, Modal } from "react-bootstrap";
import QRCode from "qrcode.react";

import { withT } from "services/translation/";

import ToggleButton from "components/Common/Buttons/Toggle";
import Icon from "components/Icons/Icon";
import Loading from "components/Loading";
import Alert from "components/Common/Alert";
import RecoveryCodes from "components/Common/RecoveryCodes/RecoveryCodes";

import {
  requestChangeMFAStart,
  requestChangeMFANotified,
  requestMFASetupStart,
  requestMFASetupNotified,
  mfaClearRecoveryCodes,
} from "services/redux/actions/";

const METHOD_EMAIL = "E";
const METHOD_AUTHENTICATOR = "A";

class TwoFactorAuth extends React.Component {
  state = {
    selectedMethod: METHOD_AUTHENTICATOR,
    verificationCode: "",
  };

  componentDidMount() {
    this.setState({ selectedMethod: this.confirmedMethod() })
  }

  // The method the backend actually holds for this user. Selecting the
  // authenticator app radio only changes selectedMethod locally until the code
  // is verified, so this is what the radio falls back to whenever an
  // unverified setup is abandoned.
  confirmedMethod = () =>
    this.props.user.multiFactorAuthType === METHOD_AUTHENTICATOR
      ? METHOD_AUTHENTICATOR
      : METHOD_EMAIL;

  componentDidUpdate(prevProps) {
    const { user, t } = this.props;
    const prevUser = prevProps.user;
    
    if (
      user.actions.changeMFA.success === true
      && prevUser.actions.changeMFA.success !== true
    ) {
      // Verifying the authenticator app succeeds and issues recovery codes in
      // the same action. The codes modal confirms the change well enough on
      // its own, so don't stack a success popup on top of it.
      const showingRecoveryCodes =
        !!user.recovery_codes && user.recovery_codes.length > 0;

      if (!showingRecoveryCodes) {
        Alert({
          text: t("user_details.profile_updated_successfully"),
          icon: "success"
        });
      }
      this.props.dispatch(requestChangeMFANotified());
    }

    if (
      user.actions.changeMFA.error && user.actions.changeMFA.error.message
      && (
        !prevUser.actions.changeMFA.error.message
        || (prevUser.actions.changeMFA.error.message && prevUser.actions.changeMFA.error.message !== user.actions.changeMFA.error.message)
      )
    ) {
      Alert({
        text: t("user_details.two_factor_auth_updated_error"),
        icon: "error"
      });
      this.props.dispatch(requestChangeMFANotified());
    }
  }

  componentWillUnmount = () => {
    this.props.dispatch(mfaClearRecoveryCodes());
  }

  submitChange = (multiFactorAuth, multiFactorAuthType, verificationCode) => {
    const { user, dispatch } = this.props;
    dispatch(
      requestChangeMFAStart({
        multiFactorAuth,
        multiFactorAuthType,
        ...(verificationCode ? { verificationCode } : {}),
        locale: user.language,
        timezone: user.timezone
      })
    );
  };

  // A secret/QR code has been issued but not yet verified. 2FA is still off
  // server-side throughout this window - multiFactorAuth only becomes "Y" once
  // the code is confirmed - so anything deciding whether 2FA is "on" for the
  // user has to take this into account as well as multiFactorAuth.
  isAuthenticatorSetupActive = () => {
    const mfaSetup = this.props.user.actions.mfaSetup || {};
    return !!(
      mfaSetup.loading ||
      mfaSetup.success === true ||
      mfaSetup.success === false
    );
  };

  startAuthenticatorSetup = () => {
    this.setState({ verificationCode: "" });
    this.props.dispatch(requestMFASetupStart());
  };

  cancelAuthenticatorSetup = () => {
    // Abandoning setup leaves the backend's method untouched, so put the radio
    // back to whatever it actually has - otherwise a user who was on email 2FA
    // is left looking at Authenticator App selected when it never took effect.
    this.setState({
      selectedMethod: this.confirmedMethod(),
      verificationCode: ""
    });
    this.props.dispatch(requestMFASetupNotified());
  };

  handleToggle = () => {
    const { user } = this.props;
    const confirmedOn = user.multiFactorAuth === "Y";

    if (confirmedOn) {
      this.submitChange("N", "N");
      return;
    }

    // The toggle reads as on during authenticator app setup too, so switching
    // it off here means "abandon the setup" rather than "disable 2FA" - 2FA was
    // never enabled server-side, so there's nothing to ask the backend for.
    // Without this the toggle looks stuck: multiFactorAuth is still "N", so we
    // would fall through and start setup all over again.
    if (this.isAuthenticatorSetupActive()) {
      this.cancelAuthenticatorSetup();
      return;
    }

    this.setState({ selectedMethod: METHOD_AUTHENTICATOR, verificationCode: "" });
    this.startAuthenticatorSetup();
  };

  handleMethodChange = method => {
    const { user } = this.props;
    this.setState({ selectedMethod: method, verificationCode: "" });

    // Changing method takes effect straight away for email, 
    // but authenticator app requires a fresh secret + verification.
    if (method === METHOD_EMAIL) {
      this.submitChange("Y", METHOD_EMAIL);
    } else {
      this.startAuthenticatorSetup();
    }
  };

  submitVerification = () => {
    this.submitChange("Y", METHOD_AUTHENTICATOR, this.state.verificationCode);
  };

  dismissRecoveryCodes = () => {
    this.props.dispatch(mfaClearRecoveryCodes());
  };

  render() {
    const { user, t } = this.props;
    const mfaLoading = user.actions.changeMFA
      ? user.actions.changeMFA.loading
      : false;
    const mfaSetup = user.actions.mfaSetup || {};
    const confirmedOn = user.multiFactorAuth === "Y";
    const authenticatorSetupActive = this.isAuthenticatorSetupActive();
    // Treat the toggle (and the method details below it) as "on" as soon as
    // the user starts enabling 2FA, even before the backend confirms it -
    // this covers the authenticator app setup/verification window, where
    // multiFactorAuth is still "N" server-side until the code is verified.
    const isOn = confirmedOn || authenticatorSetupActive || (mfaLoading && !confirmedOn);
    const busy = !!mfaLoading || !!mfaSetup.loading;

    // Show the QR/secret/verification panel whenever the authenticator app
    // method is selected and a setup attempt is in flight, succeeded (awaiting
    // the confirmation code) or failed. On successful verification the
    // reducer clears mfaSetup, which naturally hides this panel again.
    const showAuthenticatorSetup =
      this.state.selectedMethod === METHOD_AUTHENTICATOR && authenticatorSetupActive;

    // Populated only by a successful authenticator app verification, and cleared
    // by MFA_CLEAR_RECOVERY_CODES (on unmount, on dismissal) or by any later MFA
    // change - so the panel below shows exactly once, right after setup.
    const recoveryCodes = user.recovery_codes;

    return (
      <div>
        <hr />
        <Form.Row className="pl-2 mt-4 d-flex align-items-center">
          <Card.Title>{t("user_details.two_factor_auth")}</Card.Title>
          <Button
            variant="primary"
            style={{
              height: "19px",
              width: "19px",
              borderRadius: "50%",
              padding: "0",
              marginLeft: "15px",
              marginBottom: "0.75rem"
            }}
            onClick={() => {
              window.Intercom("showArticle", 12859172);
            }}
          >
            <Icon
              className="fas fa-question fa-xs"
              style={{
                position: "relative",
                bottom: "3px"
              }}
            />
          </Button>
        </Form.Row>
        <Form.Row className="pl-2">
          {t("user_details.two_factor_auth_desc")}
        </Form.Row>

        <Form.Row className="mt-3 d-flex align-items-center">
          <div>
            {/* data-toggle={null} overrides the "button" that Toggle sets by
                default (props spread after it, so this wins). bootstrap.min.js
                is loaded globally and its button plugin would otherwise toggle
                the .active class straight on the DOM node, outside React's
                knowledge - which leaves this toggle stuck on when abandoning
                an unverified setup. Unlike the dark mode toggle, isOn here is
                a real derived value, so React can own the class outright. */}
            <ToggleButton
              onClick={this.handleToggle}
              active={isOn}
              data-toggle={null}
              size="xs"
              style={{ marginTop: "-2px" }}
              disabled={busy}
            />
          </div>
          <div className="ml-2">{mfaLoading && <Loading />}</div>
        </Form.Row>

        {isOn && (
          <>
            <Form.Row className="pl-2 mt-3 mb-1">
              <small className="text-muted">
                {t("user_details.two_factor_auth_method")}
              </small>
            </Form.Row>
            <Form.Row className="pl-2 mt-2">
              <Form.Check
                inline
                type="radio"
                id="mfa-method-authenticator"
                name="mfaMethod"
                label={t("user_details.authenticator_app_auth")}
                checked={this.state.selectedMethod === METHOD_AUTHENTICATOR}
                onChange={() => this.handleMethodChange(METHOD_AUTHENTICATOR)}
                disabled={busy}
              />
              <Form.Check
                inline
                type="radio"
                id="mfa-method-email"
                name="mfaMethod"
                label={t("user_details.email_auth")}
                checked={this.state.selectedMethod === METHOD_EMAIL}
                onChange={() => this.handleMethodChange(METHOD_EMAIL)}
                disabled={busy}
              />
            </Form.Row>

            {showAuthenticatorSetup && (
              <div
                className="pl-2 mt-3 p-3"
                style={{ background: "#f8f9fa", borderRadius: "6px" }}
              >
                <div className="mb-2">
                  <strong>{t("user_details.authenticator_app_setup_title")}</strong>
                </div>
                <div className="mb-3">
                  {t("user_details.authenticator_app_setup_desc")}
                </div>

                {mfaSetup.loading && <Loading />}

                {!mfaSetup.loading && mfaSetup.success === false && (
                  <div className="mb-3">
                    <div className="text-danger mb-2">
                      {t("user_details.authenticator_app_setup_error")}
                    </div>
                    <Button variant="secondary" size="sm" onClick={this.startAuthenticatorSetup}>
                      {t("user_details.authenticator_app_verify_button")}
                    </Button>
                  </div>
                )}

                {!mfaSetup.loading && mfaSetup.success === true && (
                  <>
                    {mfaSetup.totp_provisioning_uri && (
                      <div className="mb-3">
                        <QRCode value={mfaSetup.totp_provisioning_uri} size={160} />
                      </div>
                    )}

                    {mfaSetup.secret && (
                      <div className="mb-3">
                        <div>{t("user_details.authenticator_app_manual_key")}</div>
                        <code>{mfaSetup.secret}</code>
                      </div>
                    )}

                    {/* A plain div, not Form: this already sits inside UserDetailsForm's
                        <form>, and nested <form> elements are invalid HTML. */}
                    <div>
                      <Form.Row className="align-items-center">
                        <Col md="4">
                          <Form.Control
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength={6}
                            placeholder={t(
                              "user_details.authenticator_app_verification_code"
                            )}
                            value={this.state.verificationCode}
                            onChange={e =>
                              this.setState({
                                verificationCode: e.target.value
                                  .replace(/\D/g, "")
                                  .slice(0, 6)
                              })
                            }
                            onKeyDown={e => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                if (this.state.verificationCode.length === 6 && !mfaLoading) {
                                  this.submitVerification();
                                }
                              }
                            }}
                          />
                        </Col>
                        <Col md="auto">
                          <Button
                            variant="primary"
                            type="button"
                            onClick={this.submitVerification}
                            disabled={
                              this.state.verificationCode.length !== 6 || mfaLoading
                            }
                          >
                            {mfaLoading ? <Loading /> : null}{" "}
                            {t("user_details.authenticator_app_verify_button")}
                          </Button>
                        </Col>
                        <Col md="auto">
                          <Button
                            variant="link"
                            onClick={this.cancelAuthenticatorSetup}
                          >
                            {t("user_details.authenticator_app_cancel_button")}
                          </Button>
                        </Col>
                      </Form.Row>
                    </div>
                    <div><br />{t("user_details.authenticator_app_no_app")}</div>
                  </>
                )}
              </div>
            )}

          </>
        )}

        {/* Shown in a modal on first setup so the codes can't be missed. No
            close button, static backdrop and no escape key, so the only way
            out is the action button inside RecoveryCodes - which stays
            disabled until the user confirms they've saved them. Kept outside
            the isOn block so the codes can never be stranded unseen. */}
        <Modal
          show={!!recoveryCodes && recoveryCodes.length > 0}
          onHide={this.dismissRecoveryCodes}
          backdrop="static"
          keyboard={false}
          size="lg"
          centered
        >
          <Modal.Body>
            <RecoveryCodes
              className=""
              style={{ background: "transparent" }}
              codes={recoveryCodes}
              description={t("user_details.recovery_codes_desc")}
              actionLabel={t("user_details.recovery_codes_done_button")}
              onContinue={this.dismissRecoveryCodes}
            />
          </Modal.Body>
        </Modal>
      </div>
    );
  }
}

const mapStateToProps = state => {
  return {
    user: state.user
  };
};
TwoFactorAuth = withT(
  connect(mapStateToProps)(TwoFactorAuth),
  "user.profile"
);

export { TwoFactorAuth };
